import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import type { identifier } from '@core/models/base';
import { adminSessionService } from '@shared/services/adminSession.service';

/**
 * inactive — not on a gated admin route (or feature off).
 * active   — this tab holds the seat.
 * blocked  — arrived while another tab holds the seat; modal shown.
 * kicked   — held the seat but another tab took it; modal shown over the
 *            (possibly unsaved) work.
 *
 * There is no heartbeat timer and no idle detection: background polling on many admin
 * pages keeps requests flowing whether or not a human is present, so "present" can't
 * be measured honestly. What CAN be measured is writing — only mutating requests stamp
 * the lease, and polling is all reads — so the seat moves when a human clicks take
 * over AND the holder hasn't written for the server's quiet window. An actively-working
 * session is protected from being bumped; a lost tab writes nothing, goes quiet on its
 * own, and can never deadlock the login.
 */
export type AdminSessionStatus = 'inactive' | 'active' | 'blocked' | 'kicked';

export interface AdminSessionState {
  status: AdminSessionStatus;
  studioId: identifier;
  /**
   * True once the server has actually granted this tab the seat. Distinguishes
   * "kicked mid-session" (freeze, keep unsaved work) from "blocked on arrival"
   * (reload on entry) when a conflict comes back — start() sets status
   * optimistically before the boot claim answers.
   */
  leaseConfirmed: boolean;
  claimLoading: boolean;
  /**
   * True when the last take-over click was refused because the other session made
   * changes within the server's quiet window — the gate explains the login is still
   * in use and the button stays available to try again later.
   */
  holderActive: boolean;
  /**
   * Snapshot of the holder's write activity for the gate's live status line: how many
   * seconds ago the holder last made a change (null = never), the server's quiet window,
   * and Date.now() when the snapshot was taken so the gate can age it locally between
   * checks. Aging a snapshot only ever moves it TOWARD "quiet", and the take-over click
   * re-checks the server at that moment, so a stale line can never bump an active holder.
   */
  holderSecondsSinceWrite: number | null;
  holderQuietWindowSeconds: number | null;
  holderCheckedAt: number | null;
}

export const useAdminSessionStore = defineStore('adminSession', {
  state: (): AdminSessionState => ({
    status: 'inactive',
    studioId: 0,
    leaseConfirmed: false,
    claimLoading: false,
    holderActive: false,
    holderSecondsSinceWrite: null,
    holderQuietWindowSeconds: null,
    holderCheckedAt: null,
  }),

  actions: {
    /** Called by the router guard when entering a gated admin route. */
    start(studioId: identifier) {
      // Record the studio even when a conflict already froze this tab: the page's own
      // 409s can hit onConflict() before this runs (the router guard awaits the features
      // fetch first), and takeover is dead without a studioId.
      this.studioId = studioId;

      // Never clear a conflict modal by navigating; only takeover/reload resolves it.
      if (this.status === 'blocked' || this.status === 'kicked') return;
      if (this.status === 'active') return;

      this.status = 'active';
      this.leaseConfirmed = false;
      // Boot claim: registers the seat (or surfaces the conflict modal immediately via
      // the 409 interceptor) instead of waiting for the page's first real API call.
      void this.claim();
    },

    /**
     * Called by the router guard when leaving the admin area (or when the flag is off).
     * Clears blocked/kicked too: a conflict is moot outside the gated area, and the
     * modal must not follow the user onto consumer/account pages. Coming back into the
     * admin area starts over with a fresh claim.
     */
    stop(options: { release: boolean }) {
      if (options.release && this.status === 'active' && this.studioId) {
        adminSessionService.releaseOnUnload(this.studioId);
      }
      this.status = 'inactive';
      this.studioId = 0;
      this.leaseConfirmed = false;
      this.holderActive = false;
      this.holderSecondsSinceWrite = null;
      this.holderQuietWindowSeconds = null;
      this.holderCheckedAt = null;
    },

    async claim() {
      if (!this.studioId) return;
      try {
        await adminSessionService.heartbeat(this.studioId);
        this.leaseConfirmed = true;
      } catch {
        // The 409 conflict is routed to onConflict() by the axios interceptor;
        // anything else (network blip) is left alone — the guard runs on every
        // gated request, so the next real API call settles it.
      }
    },

    /** Called by the axios response interceptor on 409 adminSessionConflict. */
    onConflict(studioId?: identifier) {
      // A conflict can arrive before the router guard has called start() (the guard
      // awaits the features fetch); the interceptor derives the studio from the failed
      // request's URL so takeover works from the very first 409.
      if (studioId && !this.studioId) this.studioId = studioId;

      if (this.status === 'kicked' || this.status === 'blocked') return;

      if (this.status === 'active' && this.leaseConfirmed) {
        // We held the seat and lost it mid-session: freeze under the modal so
        // unsaved work survives.
        this.status = 'kicked';
      } else {
        // Blocked on arrival (boot claim answered 409, or a stray gated call
        // conflicted before any seat was granted).
        this.status = 'blocked';
      }
    },

    /**
     * Moves the seat to this tab — unless the holder is actively working. A session
     * that made changes within the server's quiet window is protected: the click is
     * refused and the gate says the login is still in use. A quiet holder (dead tab,
     * crashed browser, someone who walked away) loses the seat immediately — the
     * question flow in the modal is the deliberateness gate, and a lease with no live
     * tab behind it must never require a support call to clear.
     */
    async tryTakeBack() {
      if (!this.studioId || this.claimLoading) return;
      const wasBlocked = this.status === 'blocked';
      this.claimLoading = true;
      this.holderActive = false;
      try {
        if (await this.checkHolderActivity()) {
          this.holderActive = true;
          return;
        }
        await adminSessionService.takeover(this.studioId);
        if (wasBlocked) {
          // This tab has been sitting on stale (or never-loaded) data the whole time
          // it was gated — reload for a fresh start.
          window.location.reload();
          return;
        }
        // Kicked: keep the page as-is — the whole point is preserving unsaved work.
        this.status = 'active';
        this.leaseConfirmed = true;
      } catch (error) {
        // The server enforces the quiet window too: a 409 here means the holder wrote
        // between our activity check and the takeover landing (or the check failed open
        // during an outage while the holder was genuinely active). Same handling as a
        // refused pre-check — explain, re-sync the status line, let them retry.
        if (
          isAxiosError(error) &&
          error.response?.status === 409 &&
          (error.response.data as { code?: string })?.code === 'adminSessionHolderActive'
        ) {
          this.holderActive = true;
          void this.checkHolderActivity();
          return;
        }
        // Network blip — the button stays available; clicking again retries.
      } finally {
        this.claimLoading = false;
      }
    },

    /**
     * Reads the holder's write activity, updates the snapshot the gate's live status
     * line renders from, and returns whether the holder is inside the quiet window.
     * Called on a timer while the conflict modal is open AND at the moment of a
     * take-over click — the click never trusts a stale snapshot.
     */
    async checkHolderActivity(): Promise<boolean> {
      try {
        const activity = await adminSessionService.holderActivity(this.studioId);
        this.holderSecondsSinceWrite = activity.secondsSinceLastWrite;
        this.holderQuietWindowSeconds = activity.quietWindowSeconds;
        this.holderCheckedAt = Date.now();
        return activity.holderIsWriting;
      } catch {
        // Can't read the signal — fail open. The quiet window protects a person
        // mid-work, not against outages; a network blip must never strand the login
        // behind a support call. The last snapshot is kept: aging it locally only
        // ever moves the status toward "quiet", which matches failing open.
        return false;
      }
    },

    /** window pagehide — best-effort checkout so the next tab gets straight in. */
    releaseOnUnload() {
      if (this.status === 'active' && this.studioId) {
        adminSessionService.releaseOnUnload(this.studioId);
      }
    },
  },
});
